<template>
    <div class="hoh-footer position-relative">
        <div
                :style="{background: computedOverlayColor}"
                class="overlay position-absolute"
        >
            <div class="gradient w-100 h-100" />
        </div>
        <div class="footer-wrapper d-flex flex-column align-items-center">
            <div class="footer-content-wrapper d-flex justify-content-between align-items-center w-100">
                <div class="headline d-flex flex-column justify-content-between w-100">
                    <p class="title">
                        Get Started!
                    </p>
                    <p class="subtitle">
                        Put in your address to preview your hub!
                    </p>

                    <address-type-ahead
                            v-model="address"
                            :has-focusable-state="false"
                            allow-user-value
                            class="input-group prepend-icon-input-group"
                            input-class="border-left-0 border-right-0 pr-0 pl-0"
                            placeholder-fallback="Home Address"
                            resource-type=""
                            @input="handleAddressInput"
                    >
                        <template #before>
                            <b-input-group-prepend>
                                <b-button
                                        class="d-flex align-items-center"
                                        disabled
                                >
                                    <i
                                            class="far fa-map-marker-alt"
                                    />
                                </b-button>
                            </b-input-group-prepend>
                        </template>
                        <template
                                v-if="!isMobile"
                                #after
                        >
                            <b-input-group-append class="px-1 py-1">
                                <b-button
                                        class="px-4 btn-lg mx-auto"
                                        variant="outline-primary"
                                        @click="handleClaimMyHomeClick"
                                >
                                    Claim My Home
                                </b-button>
                            </b-input-group-append>
                        </template>
                    </address-type-ahead>
                </div>
                <agent-card-hoh
                        :avatar="cardAvatar"
                        :avatar-size="6.25"
                        :company-logo="cardCompanyLogo"
                        :display-name="cardDisplayName"
                        :email="cardEmail"
                        :phones="cardPhones"
                        :social-links="cardSocialLinks"
                        :occupation="cardOccupation"
                        :loan-officer-license="cardLoanOfficerLicense"
                />
            </div>
            <div class="footer-disclaimer-wrapper d-flex justify-content-between align-items-center w-100">
                <div class="footer-disclaimer-text d-flex flex-column justify-content-between">
                    <div class="text-white">
                        <p class="notice">
                            {{ landingPageNotice }}
                            <span
                                    v-for="(link, index) in landingPageLinks"
                                    :key="`landing-page-link-${index}`"
                            >
                                <b-link
                                        :id="index"
                                        :href="link.url"
                                        class="text-white"
                                        target="_blank"
                                >
                                    {{ link.title }}
                                </b-link>
                                <span v-if="index < landingPageLinks.length - 1">
                                    |
                                </span>
                            </span>
                        </p>
                        <div class="footer-links">
                            <div class="d-sm-flex d-md-block">
                            <span class="legal-links">
                                <b-link
                                        :href="termsOfUse"
                                        class="text-white"
                                        target="_blank"
                                >
                                    Terms of Use
                                </b-link>
                                |
                                <b-link
                                        :href="privacyPolicyUrl"
                                        class="text-white"
                                        target="_blank"
                                >
                                    Privacy Policy
                                </b-link>
                            </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="equal-housing-icons-wrapper d-flex justify-content-between w-100">
                    <b-img
                            :src="require('@milestoneslabs/milestones-ui-kit/src/assets/images/workspace-footer/equal-housing-opportunity.png')"
                            class="equal-housing"
                            fluid
                            width="72"
                    />
                    <b-img
                            :src="require('@milestoneslabs/milestones-ui-kit/src/assets/images/workspace-footer/equal-housing-lender.png')"
                            class="equal-housing"
                            fluid
                            width="72"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import './LandingPageFooterHoh.scss';
import './LandingPageFooterHoh.mobile.scss';
import {privacyPolicy, termsOfUse} from "@milestoneslabs/milestones-ui-kit/src/helpers/externalLinks";
import AgentCardHoh from "../../AgentCardHoh/AgentCardHoh";
import AddressTypeAhead from "@milestoneslabs/milestones-ui-kit/src/components/AddressTypeAhead/AddressTypeAhead";
import QueryStrings from "@milestoneslabs/milestones-unified-login/src/QueryStrings";
import LandingPageAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageAppResource";
import {
    DESTINED_TO as PHONE_DESTINED_TO
} from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPagePhoneAppResource";
import {
    DESTINED_TO as SOCIAL_LINK_DESTINED_TO
} from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageSocialLinkAppResource";
import IsMobileMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/IsMobileMixin";
import HOHAddressMixin from "../../Home/HomeTypes/HomeOwnershipHome/HOHAddressMixin";
import FileAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/FileAppResource";

export default {
    name: "LandingPageFooterHoh",

    components: {
        AgentCardHoh,
        AddressTypeAhead,
    },
    mixins: [
        IsMobileMixin,
        HOHAddressMixin,
    ],

    props: {
        landingPage: {
            type: LandingPageAppResource,
            required: true
        },
    },

    data() {
        return {
            address: null,
            termsOfUse,
        };
    },

    computed: {
        year() {
            return (new Date).getFullYear();
        },
        computedOverlayColor() {
            return this.landingPage.primaryColor || '#3D465E';
        },
        privacyPolicyUrl() {
            if (window.organizationPrivacyPolicyUrl) {
                return window.organizationPrivacyPolicyUrl;
            }

            return privacyPolicy;
        },
        cardAvatar() {
            return this.landingPage?.avatar || new FileAppResource({});
        },
        cardCompanyLogo() {
            return this.landingPage?.organizationLogo?.publicStorageUrl || '';
        },
        cardDisplayName() {
            return this.landingPage.firstName + ' ' + this.landingPage.lastName;
        },
        cardEmail() {
            return this.landingPage.email || '';
        },
        cardPhones() {
            return this.landingPage?.phones ?
                this.landingPage.phones.toArray().filter(item => {
                    return item.destinedTo === PHONE_DESTINED_TO.REAL_ESTATE_AGENT;
                }) :
                [];
        },
        cardSocialLinks() {
            return this.landingPage?.socialLinks ?
                this.landingPage.socialLinks.toArray().filter(item => {
                    return item.destinedTo === SOCIAL_LINK_DESTINED_TO.REAL_ESTATE_AGENT;
                }) :
                [];
        },
        cardOccupation() {
            return this.landingPage?.sell?.projectMembershipType ?
                {
                    name: this.landingPage.sell.projectMembershipType.name,
                    slug: this.landingPage.sell.projectMembershipType.slug,
                } :
                {
                    name: '',
                    slug: '',
                };

        },
        cardLoanOfficerLicense() {
            return this.landingPage?.user?.loanOfficerLicense || {};
        },
        landingPageLinks() {
            let links = [];
            if (!this.landingPage?.links?.appResources) {
                return links;
            }
            Object.values(this.landingPage.links.appResources).forEach((link) => {
                links.push({
                    title: link.title,
                    url: link.url,
                });
            });
            return links;
        },
        landingPageNotice() {
            return this.landingPage?.notice || '';
        },
    },

    methods: {
        handleAddressInput(address) {
            if (!address) {
                console.warn('Address is not valid: ', address);

                return;
            }

            this.$router.push({
                name: 'landing-page.hub-preview',
                query: {
                    [QueryStrings.HOH_ADDRESS]: this.getEncodedAddress(address)
                }
            });
        },
        handleClaimMyHomeClick() {
            if (!this.address) {
                console.warn('handleClaimMyHomeClick Address is not valid: ', this.address);
                return;
            }
            this.$router.push({
                name: 'landing-page.hub-preview',
                query: {
                    [QueryStrings.HOH_ADDRESS]: this.getEncodedAddress(this.address)
                }
            });
        },
    }
};
</script>
